// Module frontend surface.
//
// The shell discovers this file automatically (modules/*/frontend/index.js) and
// merges what it exports. Adding this module needs no edit to the shell.
//
// Four routes and one menu entry. There is no «mis incidencias» route of its
// own: `/incidents?mine=true` already filters by person, and a second screen
// showing the same rows with one filter pre-set is a second screen to keep in
// step with the first.

import { withAnalytics } from './i18n/analytics.js'

export const routes = [
  {
    path: '/incidents',
    name: 'incidents',
    component: () => import('./pages/IncidentsPage.vue'),
    meta: { permission: 'incidents:read', title: 'incidents.title' },
  },
  // The static path wins over /:id by route ranking, so the order here does not
  // matter — but new sits first anyway, for the reader.
  {
    path: '/incidents/new',
    name: 'incident-new',
    component: () => import('./pages/IncidentFormPage.vue'),
    meta: { permission: 'incidents:report', title: 'incidents.newTitle' },
  },
  {
    path: '/incidents/:id',
    name: 'incident',
    component: () => import('./pages/IncidentPage.vue'),
    // `report` as well, so somebody who may only write one down can still open
    // the one they wrote and follow what happened to it.
    meta: { permission: ['incidents:read', 'incidents:report'], title: 'incidents.detailTitle' },
  },
  {
    path: '/incidents/:id/edit',
    name: 'incident-edit',
    component: () => import('./pages/IncidentFormPage.vue'),
    // Editing what an incident SAYS is `handle` and not `report`: correcting
    // somebody else's account of what happened is not the same right as
    // writing your own.
    meta: { permission: 'incidents:handle', title: 'incidents.editTitle' },
  },
]

export const navigation = [
  {
    to: '/incidents',
    labelKey: 'incidents.title',
    permission: 'incidents:read',
    icon: 'alert',
    group: 'business',
  },
]

// Strings this module contributes, merged per locale. Four languages from the
// first commit (§16), including what this module is called on the PLATFORM's own
// screens — Roles, Settings, Audit and the assistant panel — because a module
// that makes somebody add keys to the shell is not extractable.
const baseMessages = {
  es: {
    // Como se llama este modulo en las pantallas de la plataforma
    // (el selector de la demo, los roles, la auditoria). Sin esto
    // salen listando el slug en ingles.
    modules: { incidents: 'Incidencias' },
    incidents: {
      // Los avisos de la mañana y de la tarjeta «Hoy» (backend/nudges.js).
      nudges: {
        critical: 'una incidencia crítica abierta | {count} incidencias críticas abiertas',
        blocking: 'una incidencia parando el trabajo | {count} incidencias parando el trabajo',
      },
      assistant: {
        // La respuesta que contestan las reglas (queries.js).
        query: {
          open: {
            some: 'Hay {count} incidencias abiertas: {list}.',
            none: 'No hay ninguna incidencia abierta.',
          },
        },
        resolve: {
          action: 'Resolver incidencia',
          field: { incident: 'Incidencia', resolution: 'Qué se hizo', severity: 'Gravedad' },
          where: 'Incidencia sobre',
          notFound: 'No se encuentra esa incidencia',
          alreadyClosed: 'Ya está cerrada',
          outcome: 'Incidencia {code} resuelta',
        },
        report: {
          summary: {
            plain: 'Nueva incidencia en {subject}: {title}',
            blocking: 'Nueva incidencia en {subject}: {title} — impide trabajar',
          },
          action: 'Abrir incidencia',
          field: {
            title: 'Qué pasa',
            description: 'Detalle',
            subject: 'Sobre',
            severity: 'Gravedad',
            blocking: 'Impide trabajar',
          },
          value: {
            yes: 'Sí',
          },
          where: 'Incidencia sobre',
          outcome: 'Incidencia {code} creada',
        },
      },
      title: 'Incidencias',
      subtitle: 'Lo que va mal, desde que alguien lo ve hasta que alguien dice qué pasó',
      // La explicacion entera del modulo. El TEXTO vive en `help/<idioma>.md`
      // —el mismo fichero que compila el manual del cliente—; aqui solo van
      // las dos frases que el indice de la ayuda necesita antes de abrirlo.
      help: {
        title: "Incidencias",
        subtitle: "Apuntar que algo va mal antes de que nadie haya decidido nada, y que quede escrito qué pasó",
      },
      detailTitle: 'Incidencia',
      newTitle: 'Nueva incidencia',
      dictate: {
        title: 'Dictarla',
        subtitle: 'Grábala y se rellena sola. Luego la repasas y la guardas.',
        clip: 'La nota de voz',
        read: 'Rellenar con lo que he dicho',
        heard: 'Lo que se ha oído',
        silence: 'No se ha oído nada. Vuelve a grabarla, más cerca de la boca.',
        which: '«{words}»: hay varias. ¿Cuál es?',
        notFound: 'No he encontrado «{words}». Escógela abajo a mano.',
        keeps: 'La grabación se queda colgada de la incidencia al guardarla. Quítala con la × si no la quieres.',
        clipFailed: 'La incidencia se guardó, pero la nota de voz no se pudo adjuntar.',
      },
      editTitle: 'Editar la incidencia',
      blocking: 'Impide trabajar',
      stale: 'Sin tocar',

      related: {
        // Lo que se lee en la pestaña de la ficha de una máquina, un
        // emplazamiento o una orden. «Qué le pasa» y no «Incidencias»: en la
        // ficha de una prensa la pregunta no es qué incidencias existen, es qué
        // le pasa a esa prensa ahora mismo.
        open: 'Qué le pasa',
        customer: 'Incidencias',
      },

      actions: {
        new: 'Nueva incidencia',
        take: 'Me la quedo',
        resolve: 'Resolver',
        createOrder: 'Crear orden',
        dismiss: 'Descartar',
        edit: 'Editar',
        reopen: 'Reabrir',
      },

      status: {
        open: 'Abierta',
        in_progress: 'En curso',
        resolved: 'Resuelta',
        dismissed: 'Descartada',
      },

      severity: { low: 'Leve', medium: 'Normal', high: 'Grave', critical: 'Crítica' },

      subjectType: {
        asset: 'Activo',
        property: 'Emplazamiento',
        production_order: 'Orden de fabricación',
        vehicle: 'Vehículo',
        location: 'Ubicación',
        project: 'Proyecto',
        work: 'Obra',
      },

      stats: {
        open: 'Abiertas',
        critical: 'Críticas',
        blocking: 'Paran trabajo',
        stale: 'Sin tocar',
      },

      filters: {
        search: 'Buscar',
        searchPlaceholder: 'Lo que pone, el código o sobre qué es',
        status: 'Estado',
        statusAny: 'Cualquiera',
        severity: 'Gravedad',
        severityAny: 'Cualquiera',
        type: 'Tipo',
        typeAny: 'Cualquiera',
        subjectType: 'Sobre qué',
        subjectTypeAny: 'Cualquier cosa',
        blocking: 'Trabajo parado',
        blockingOnly: 'Solo las que paran trabajo',
      },

      columns: {
        code: 'Código',
        incident: 'Incidencia',
        severity: 'Gravedad',
        status: 'Estado',
        assignee: 'Responsable',
        age: 'Lleva',
      },

      age: {
        today: 'Hoy',
        underHour: 'Menos de una hora',
        hours: '{hours} horas',
        days: '{days} días',
        months: 'Hace {months} meses',
      },

      empty: {
        title: 'No hay nada apuntado',
        hint: 'Apunta lo que veas y se quedará aquí hasta que alguien diga qué pasó.',
      },

      pager: { previous: 'Anterior', next: 'Siguiente', page: 'Página {page} de {of}' },

      outcome: {
        resolved: 'Resuelta por {who} el {when}',
        dismissed: 'Descartada por {who} el {when}',
        somebody: 'alguien',
      },

      facts: {
        title: 'Datos',
        about: 'Sobre',
        reported: 'Cuándo pasó',
        reportedBy: 'Quien lo vio',
        assignee: 'Responsable',
        nobody: 'Nadie',
        openFor: 'Lleva abierta',
        took: 'Tardó',
        customer: 'Cliente',
        customerUnnamed: 'Ver el cliente',
        imported: 'Origen',
        importedFrom: 'Importada de {system}',
        importedFromCode: 'Importada de {system} ({code})',
        recordedThere: 'registrada allí el {date}',
      },

      history: {
        title: 'Historial',
        reported: 'Incidencia abierta',
        updated: 'Modificada',
        assigned: 'Asignada',
        note: 'Nota',
        severity: 'Gravedad: de {from} a {to}',
        add: 'Añadir',
        addPlaceholder: 'Cuenta cómo va',
      },

      files: {
        title: 'Fotos y papeles',
        empty: 'Nada adjunto. Una foto dice de un vistazo lo que la frase tarda un párrafo.',
        add: 'Adjuntar',
        uploading: 'Subiendo…',
        remove: 'Quitar',
        full: 'Ya tiene {max} ficheros, que es el tope. Quita alguno para añadir otro.',
      },

      confirm: {
        take: '¿Te quedas esta incidencia?',
        detachTitle: 'Quitar el fichero',
        detachMessage: '«{name}» dejará de estar en esta incidencia.',
        reopenTitle: 'Reabrir la incidencia',
        reopenMessage: 'Reabrir es para una que no quedó arreglada, o que se cerró por error. Si ha vuelto a pasar, abre una nueva: así queda cuánto tiempo aguantó.',
      },

      resolve: {
        title: 'Resolver la incidencia',
        what: 'Qué se ha hecho',
        hint: 'Esta frase es lo que leerá alguien la próxima vez que pase lo mismo.',
        placeholder: 'Cambiado el retén y apretado el racor',
        cancel: 'Volver',
        confirm: 'Resolver',
      },

      dismiss: {
        title: 'Descartar la incidencia',
        why: 'Por qué no se va a tocar',
        hint: 'Descartar no es borrar: la incidencia se queda con este motivo y con quien lo dijo.',
        placeholder: 'Era el nivel de la mirilla, no una fuga',
        cancel: 'Volver',
        confirm: 'Descartar',
      },

      toast: {
        reported: 'Incidencia abierta',
        saved: 'Guardada',
        taken: 'Es tuya',
        resolved: 'Resuelta',
        dismissed: 'Descartada',
        reopened: 'Reabierta',
        attached: 'Fichero adjuntado',
      },

      form: {
        subtitle: 'Lo que has visto, y sobre qué',
        about: 'Sobre qué es',
        aboutHint: 'Lo primero, porque es lo único que no se puede deducir después.',
        subjectType: 'Tipo de cosa',
        subjectFree: 'Escribirlo a mano',
        subject: 'Cuál',
        subjectPlaceholder: 'Busca la máquina, el sitio o la orden',
        subjectName: 'Sobre qué',
        subjectNameHint: 'Escríbelo tal y como lo llamáis. Vale igual que elegirlo de una lista.',
        subjectNamePlaceholder: 'Prensa 3',
        needsSubject: 'Dile sobre qué es: sin eso, es una frase que nadie puede colocar.',
        what: 'Qué ha pasado',
        title: 'En una línea',
        titleHint: 'Como se lo contarías a un compañero.',
        titlePlaceholder: 'Pierde aceite por el lateral',
        description: 'Detalle',
        type: 'Tipo',
        typeAny: 'Sin tipo',
        when: 'Cuándo pasó',
        whenHint: 'Si fue antes, ponlo: todo lo que se mide cuelga de esta fecha.',
        howBad: 'Cómo de grave',
        howBadHint: 'La gravedad decide a quién se avisa. Que no se avise por todo es lo que hace que se lea el aviso.',
        severity: 'Gravedad',
        blocking: 'Trabajo parado',
        blockingHint: 'Solo si de verdad no se puede seguir.',
        blockingLabel: 'Impide seguir trabajando',
        assignee: 'Responsable',
        assigneeHint: 'Opcional. Si no pones a nadie, se queda esperando a que alguien se la quede.',
        assigneePlaceholder: 'Busca una persona',
        media: 'Fotos y vídeos',
        mediaHint: 'Hasta {max} ficheros contando la nota de voz. Se suben cuando guardes, uno a uno. Es lo que distingue esta de las otras tres veces que alguien escribió lo mismo.',
        mediaFailed: 'La incidencia se guardó, pero no se pudo subir un fichero. Cuélgalo desde su ficha. | La incidencia se guardó, pero no se pudieron subir {count} ficheros. Cuélgalos desde su ficha.',
        save: 'Guardar',
        discard: 'Descartar',
        leaveTitle: 'Te dejas la incidencia a medias',
        leaveMessage: 'Lo que has escrito no se ha guardado.',
        leaveConfirm: 'Salir igual',
        leaveCancel: 'Seguir aquí',
      },

      dashboard: {
        open: 'Incidencias abiertas',
        openHelp: 'Lo que va mal y todavía no ha resuelto nadie',
        critical: 'Críticas',
        criticalHelp: 'Las abiertas marcadas como críticas. Van aparte porque «hay 40 abiertas» tapa «hay 2 críticas»',
        blocking: 'Paran trabajo',
        blockingHelp: 'Las abiertas que impiden seguir trabajando ahora mismo',
        stale: 'Sin tocar',
        staleHelp: 'Abiertas desde hace más días de los configurados. Mide si el módulo se usa o solo se rellena',
        mine: 'Mis incidencias',
        mineHelp: 'Las abiertas de las que eres responsable',
      },

      notifications: {
        category: 'Incidencias',
        reported: {
          low: 'Incidencia leve',
          medium: 'Incidencia',
          high: 'Incidencia grave',
          critical: 'Incidencia crítica',
          body: '{where}: {what} ({code})',
        },
        assigned: {
          title: 'Una incidencia es tuya',
          body: '{where}: {what} ({code})',
        },
        resolved: {
          title: 'Lo que viste ya está arreglado',
          body: '{code} · {what} — {why}',
        },
        dismissed: {
          title: 'Lo que viste no se va a tocar',
          body: '{code} · {what} — {why}',
        },
      },
    },

    assistant: {
      openers: {
        incidents: {
          'what-is-open': '¿Qué hay sin resolver?',
          'stopping-work': '¿Hay algo parando trabajo ahora mismo?',
          'worst-thing': '¿Qué nos ha dado más guerra este año?',
        },
      },
    },

    permissions: {
      incidents: {
        read: 'Ver las incidencias y lo que le pasa a cada cosa',
        report: 'Abrir incidencias y añadir notas y fotos',
        handle: 'Quedárselas, cambiarles la gravedad, resolverlas y descartarlas',
        import: 'Importar las incidencias de otro sistema',
      },
    },

    settings: {
      ns: {
        incidents: {
          title: 'Incidencias',
          subtitle: 'Cómo clasificáis lo que va mal, y a quién se avisa',
          types: {
            label: 'Los tipos de incidencia que apuntáis',
            help: 'Vuestros, no nuestros, y opcional: si lo dejas vacío una incidencia es la frase que escribió alguien. Rellenarlo es lo que hace que «lo que más falla» tenga respuesta.',
          },
          alertRoles: {
            label: 'A quién se avisa cuando se abre una',
            help: 'Si lo dejas vacío no se avisa a nadie: la incidencia sigue en la lista, simplemente no suena en ningún sitio.',
          },
          notifyFrom: {
            label: 'Avisar a partir de esta gravedad',
            help: 'Una empresa donde cada rasguño avisa al jefe de planta es una empresa donde el jefe de planta lo silencia, y entonces la crítica tampoco llega.',
          },
          staleDays: {
            label: 'Días para que una abierta cuente como olvidada',
            help: 'Solo sirve para llamar la atención: nada se cierra ni se escala solo.',
          },
          maxAttachments: {
            label: 'Ficheros que aguanta una incidencia',
            help: 'Fotos, vídeos, notas de voz y papeles, todo junto. El formulario deja de ofrecer la cámara al llegar al tope, y el servidor rechaza uno más.',
          },
          resolveFromWorkOrder: {
            label: 'Resolverla al completar la orden que salió de ella',
            help: 'Apagado, la orden completada solo deja una nota en la incidencia y alguien la resuelve diciendo qué se hizo. Encendido, se resuelve sola con lo que escribieron los partes; si no escribieron nada, se queda la nota.',
          },
        },
      },
    },

    audit: {
      actions: {
        incidents: {
          incident_reported: 'Incidencia abierta',
          incident_updated: 'Incidencia modificada',
          incident_assigned: 'Incidencia asignada',
          incident_severity: 'Gravedad cambiada',
          incident_in_progress: 'Incidencia cogida',
          incident_resolved: 'Incidencia resuelta',
          incident_dismissed: 'Incidencia descartada',
          incident_open: 'Incidencia reabierta',
          incident_noted: 'Nota en una incidencia',
          incident_attached: 'Fichero adjuntado a una incidencia',
          incident_detached: 'Fichero quitado de una incidencia',
          incident_imported: 'Incidencia importada de otro sistema',
        },
      },
    },
  },

  ca: {
    // Como se llama este modulo en las pantallas de la plataforma
    // (el selector de la demo, los roles, la auditoria). Sin esto
    // salen listando el slug en ingles.
    modules: { incidents: 'Incidencies' },
    incidents: {
      // Los avisos de la mañana y de la tarjeta «Hoy» (backend/nudges.js).
      nudges: {
        critical: 'una incidència crítica oberta | {count} incidències crítiques obertes',
        blocking: 'una incidència aturant la feina | {count} incidències aturant la feina',
      },
      assistant: {
        // La respuesta que contestan las reglas (queries.js).
        query: {
          open: {
            some: 'Hi ha {count} incidències obertes: {list}.',
            none: 'No hi ha cap incidència oberta.',
          },
        },
        resolve: {
          action: 'Resoldre incidència',
          field: { incident: 'Incidència', resolution: 'Què s\'ha fet', severity: 'Gravetat' },
          where: 'Incidència sobre',
          notFound: 'No es troba aquesta incidència',
          alreadyClosed: 'Ja està tancada',
          outcome: 'Incidència {code} resolta',
        },
        report: {
          summary: {
            plain: 'Nova incidència a {subject}: {title}',
            blocking: 'Nova incidència a {subject}: {title} — impedeix treballar',
          },
          action: 'Obrir incidència',
          field: {
            title: 'Què passa',
            description: 'Detall',
            subject: 'Sobre',
            severity: 'Gravetat',
            blocking: 'Impedeix treballar',
          },
          value: {
            yes: 'Sí',
          },
          where: 'Incidència sobre',
          outcome: 'Incidència {code} creada',
        },
      },
      title: 'Incidències',
      subtitle: 'El que va malament, des que algú ho veu fins que algú diu què va passar',
      // La explicacion entera del modulo. El TEXTO vive en `help/<idioma>.md`
      // —el mismo fichero que compila el manual del cliente—; aqui solo van
      // las dos frases que el indice de la ayuda necesita antes de abrirlo.
      help: {
        title: "Incidències",
        subtitle: "Apuntar que alguna cosa va malament abans que ningú hagi decidit res, i que quedi escrit què va passar",
      },
      detailTitle: 'Incidència',
      newTitle: 'Nova incidència',
      dictate: {
        title: 'Dictar-la',
        subtitle: 'Grava-la i s\'omple sola. Després la repasses i la deses.',
        clip: 'La nota de veu',
        read: 'Omplir amb el que he dit',
        heard: 'El que s\'ha sentit',
        silence: 'No s\'ha sentit res. Torna a gravar-la, més a prop de la boca.',
        which: '«{words}»: n\'hi ha unes quantes. Quina és?',
        notFound: 'No he trobat «{words}». Tria-la a baix a mà.',
        keeps: 'La gravació queda penjada de la incidència en desar-la. Treu-la amb la × si no la vols.',
        clipFailed: 'La incidència s\'ha desat, però la nota de veu no s\'ha pogut adjuntar.',
      },
      editTitle: 'Editar la incidència',
      blocking: 'Impedeix treballar',
      stale: 'Sense tocar',

      related: {
        open: 'Què li passa',
        customer: 'Incidències',
      },

      actions: {
        new: 'Nova incidència',
        take: 'Me la quedo',
        resolve: 'Resoldre',
        createOrder: 'Crear ordre',
        dismiss: 'Descartar',
        edit: 'Editar',
        reopen: 'Reobrir',
      },

      status: {
        open: 'Oberta',
        in_progress: 'En curs',
        resolved: 'Resolta',
        dismissed: 'Descartada',
      },

      severity: { low: 'Lleu', medium: 'Normal', high: 'Greu', critical: 'Crítica' },

      subjectType: {
        asset: 'Actiu',
        property: 'Emplaçament',
        production_order: 'Ordre de fabricació',
        vehicle: 'Vehicle',
        location: 'Ubicació',
        project: 'Projecte',
        work: 'Obra',
      },

      stats: {
        open: 'Obertes',
        critical: 'Crítiques',
        blocking: 'Aturen feina',
        stale: 'Sense tocar',
      },

      filters: {
        search: 'Cercar',
        searchPlaceholder: 'El que hi diu, el codi o sobre què és',
        status: 'Estat',
        statusAny: 'Qualsevol',
        severity: 'Gravetat',
        severityAny: 'Qualsevol',
        type: 'Tipus',
        typeAny: 'Qualsevol',
        subjectType: 'Sobre què',
        subjectTypeAny: 'Qualsevol cosa',
        blocking: 'Feina aturada',
        blockingOnly: 'Només les que aturen feina',
      },

      columns: {
        code: 'Codi',
        incident: 'Incidència',
        severity: 'Gravetat',
        status: 'Estat',
        assignee: 'Responsable',
        age: 'Fa',
      },

      age: {
        today: 'Avui',
        underHour: 'Menys d’una hora',
        hours: '{hours} hores',
        days: '{days} dies',
        months: 'Fa {months} mesos',
      },

      empty: {
        title: 'No hi ha res apuntat',
        hint: 'Apunta el que vegis i es quedarà aquí fins que algú digui què va passar.',
      },

      pager: { previous: 'Anterior', next: 'Següent', page: 'Pàgina {page} de {of}' },

      outcome: {
        resolved: 'Resolta per {who} el {when}',
        dismissed: 'Descartada per {who} el {when}',
        somebody: 'algú',
      },

      facts: {
        title: 'Dades',
        about: 'Sobre',
        reported: 'Quan va passar',
        reportedBy: 'Qui ho va veure',
        assignee: 'Responsable',
        nobody: 'Ningú',
        openFor: 'Fa oberta',
        took: 'Va tardar',
        customer: 'Client',
        customerUnnamed: 'Veure el client',
        imported: 'Origen',
        importedFrom: 'Importada de {system}',
        importedFromCode: 'Importada de {system} ({code})',
        recordedThere: 'registrada allà el {date}',
      },

      history: {
        title: 'Historial',
        reported: 'Incidència oberta',
        updated: 'Modificada',
        assigned: 'Assignada',
        note: 'Nota',
        severity: 'Gravetat: de {from} a {to}',
        add: 'Afegir',
        addPlaceholder: 'Explica com va',
      },

      files: {
        title: 'Fotos i papers',
        empty: 'Res adjunt. Una foto diu d’un cop d’ull el que la frase tarda un paràgraf.',
        add: 'Adjuntar',
        uploading: 'Pujant…',
        remove: 'Treure',
        full: 'Ja té {max} fitxers, que és el límit. Treu-ne algun per afegir-ne un altre.',
      },

      confirm: {
        take: 'Et quedes aquesta incidència?',
        detachTitle: 'Treure el fitxer',
        detachMessage: '«{name}» deixarà d’estar en aquesta incidència.',
        reopenTitle: 'Reobrir la incidència',
        reopenMessage: 'Reobrir és per a una que no va quedar arreglada, o que es va tancar per error. Si ha tornat a passar, obre’n una de nova: així queda quant de temps va aguantar.',
      },

      resolve: {
        title: 'Resoldre la incidència',
        what: 'Què s’ha fet',
        hint: 'Aquesta frase és el que llegirà algú la propera vegada que passi el mateix.',
        placeholder: 'Canviat el retén i estret el racord',
        cancel: 'Tornar',
        confirm: 'Resoldre',
      },

      dismiss: {
        title: 'Descartar la incidència',
        why: 'Per què no es tocarà',
        hint: 'Descartar no és esborrar: la incidència es queda amb aquest motiu i amb qui ho va dir.',
        placeholder: 'Era el nivell de la mirilla, no una fuita',
        cancel: 'Tornar',
        confirm: 'Descartar',
      },

      toast: {
        reported: 'Incidència oberta',
        saved: 'Desada',
        taken: 'És teva',
        resolved: 'Resolta',
        dismissed: 'Descartada',
        reopened: 'Reoberta',
        attached: 'Fitxer adjuntat',
      },

      form: {
        subtitle: 'El que has vist, i sobre què',
        about: 'Sobre què és',
        aboutHint: 'El primer, perquè és l’únic que no es pot deduir després.',
        subjectType: 'Tipus de cosa',
        subjectFree: 'Escriure-ho a mà',
        subject: 'Quina',
        subjectPlaceholder: 'Cerca la màquina, el lloc o l’ordre',
        subjectName: 'Sobre què',
        subjectNameHint: 'Escriu-ho tal com en dieu. Val igual que triar-ho d’una llista.',
        subjectNamePlaceholder: 'Premsa 3',
        needsSubject: 'Digues sobre què és: sense això, és una frase que ningú pot col·locar.',
        what: 'Què ha passat',
        title: 'En una línia',
        titleHint: 'Com l’hi explicaries a un company.',
        titlePlaceholder: 'Perd oli pel lateral',
        description: 'Detall',
        type: 'Tipus',
        typeAny: 'Sense tipus',
        when: 'Quan va passar',
        whenHint: 'Si va ser abans, posa-ho: tot el que es mesura penja d’aquesta data.',
        howBad: 'Com de greu',
        howBadHint: 'La gravetat decideix a qui s’avisa. Que no s’avisi per tot és el que fa que es llegeixi l’avís.',
        severity: 'Gravetat',
        blocking: 'Feina aturada',
        blockingHint: 'Només si de debò no es pot continuar.',
        blockingLabel: 'Impedeix continuar treballant',
        assignee: 'Responsable',
        assigneeHint: 'Opcional. Si no hi poses ningú, es queda esperant que algú se la quedi.',
        assigneePlaceholder: 'Cerca una persona',
        media: 'Fotos i vídeos',
        mediaHint: 'Fins a {max} fitxers comptant la nota de veu. Es pugen quan desis, un a un. És el que distingeix aquesta de les altres tres vegades que algú va escriure el mateix.',
        mediaFailed: 'La incidència s\'ha desat, però no s\'ha pogut pujar un fitxer. Penja\'l des de la seva fitxa. | La incidència s\'ha desat, però no s\'han pogut pujar {count} fitxers. Penja\'ls des de la seva fitxa.',
        save: 'Desar',
        discard: 'Descartar',
        leaveTitle: 'Et deixes la incidència a mitges',
        leaveMessage: 'El que has escrit no s’ha desat.',
        leaveConfirm: 'Sortir igualment',
        leaveCancel: 'Seguir aquí',
      },

      dashboard: {
        open: 'Incidències obertes',
        openHelp: 'El que va malament i encara no ha resolt ningú',
        critical: 'Crítiques',
        criticalHelp: 'Les obertes marcades com a crítiques. Van a part perquè «hi ha 40 obertes» tapa «hi ha 2 crítiques»',
        blocking: 'Aturen feina',
        blockingHelp: 'Les obertes que impedeixen continuar treballant ara mateix',
        stale: 'Sense tocar',
        staleHelp: 'Obertes des de fa més dies dels configurats. Mesura si el mòdul s’usa o només s’omple',
        mine: 'Les meves incidències',
        mineHelp: 'Les obertes de les quals ets responsable',
      },

      notifications: {
        category: 'Incidències',
        reported: {
          low: 'Incidència lleu',
          medium: 'Incidència',
          high: 'Incidència greu',
          critical: 'Incidència crítica',
          body: '{where}: {what} ({code})',
        },
        assigned: {
          title: 'Una incidència és teva',
          body: '{where}: {what} ({code})',
        },
        resolved: {
          title: 'Allò que vas veure ja està arreglat',
          body: '{code} · {what} — {why}',
        },
        dismissed: {
          title: 'Allò que vas veure no es tocarà',
          body: '{code} · {what} — {why}',
        },
      },
    },

    assistant: {
      openers: {
        incidents: {
          'what-is-open': 'Què hi ha sense resoldre?',
          'stopping-work': 'Hi ha res aturant feina ara mateix?',
          'worst-thing': 'Què ens ha donat més guerra aquest any?',
        },
      },
    },

    permissions: {
      incidents: {
        read: 'Veure les incidències i què li passa a cada cosa',
        report: 'Obrir incidències i afegir-hi notes i fotos',
        handle: 'Quedar-se-les, canviar-los la gravetat, resoldre-les i descartar-les',
        import: 'Importar les incidències d’un altre sistema',
      },
    },

    settings: {
      ns: {
        incidents: {
          title: 'Incidències',
          subtitle: 'Com classifiqueu el que va malament, i a qui s’avisa',
          types: {
            label: 'Els tipus d’incidència que apunteu',
            help: 'Vostres, no nostres, i opcional: si ho deixes buit una incidència és la frase que va escriure algú. Omplir-ho és el que fa que «el que més falla» tingui resposta.',
          },
          alertRoles: {
            label: 'A qui s’avisa quan se n’obre una',
            help: 'Si ho deixes buit no s’avisa ningú: la incidència segueix a la llista, simplement no sona enlloc.',
          },
          notifyFrom: {
            label: 'Avisar a partir d’aquesta gravetat',
            help: 'Una empresa on cada esgarrapada avisa el cap de planta és una empresa on el cap de planta ho silencia, i llavors la crítica tampoc arriba.',
          },
          staleDays: {
            label: 'Dies perquè una d’oberta compti com oblidada',
            help: 'Només serveix per cridar l’atenció: res no es tanca ni s’escala sol.',
          },
          maxAttachments: {
            label: 'Fitxers que admet una incidència',
            help: 'Fotos, vídeos, notes de veu i papers, tot junt. El formulari deixa d\'oferir la càmera en arribar al límit, i el servidor en rebutja un més.',
          },
          resolveFromWorkOrder: {
            label: 'Resoldre-la en completar l\'ordre que en va sortir',
            help: 'Apagat, l\'ordre completada només deixa una nota a la incidència i algú la resol dient què s\'ha fet. Encès, es resol sola amb el que van escriure els parts; si no van escriure res, queda la nota.',
          },
        },
      },
    },

    audit: {
      actions: {
        incidents: {
          incident_reported: 'Incidència oberta',
          incident_updated: 'Incidència modificada',
          incident_assigned: 'Incidència assignada',
          incident_severity: 'Gravetat canviada',
          incident_in_progress: 'Incidència agafada',
          incident_resolved: 'Incidència resolta',
          incident_dismissed: 'Incidència descartada',
          incident_open: 'Incidència reoberta',
          incident_noted: 'Nota en una incidència',
          incident_attached: 'Fitxer adjuntat a una incidència',
          incident_detached: 'Fitxer tret d’una incidència',
          incident_imported: 'Incidència importada d’un altre sistema',
        },
      },
    },
  },

  en: {
    // Como se llama este modulo en las pantallas de la plataforma
    // (el selector de la demo, los roles, la auditoria). Sin esto
    // salen listando el slug en ingles.
    modules: { incidents: 'Incidents' },
    incidents: {
      // Los avisos de la mañana y de la tarjeta «Hoy» (backend/nudges.js).
      nudges: {
        critical: 'one critical incident open | {count} critical incidents open',
        blocking: 'one incident stopping work | {count} incidents stopping work',
      },
      assistant: {
        // La respuesta que contestan las reglas (queries.js).
        query: {
          open: {
            some: '{count} incidents are open: {list}.',
            none: 'No incident is open.',
          },
        },
        resolve: {
          action: 'Resolve an incident',
          field: { incident: 'Incident', resolution: 'What was done', severity: 'Severity' },
          where: 'Incident about',
          notFound: 'That incident was not found',
          alreadyClosed: 'It is already closed',
          outcome: 'Incident {code} resolved',
        },
        report: {
          summary: {
            plain: 'New incident on {subject}: {title}',
            blocking: 'New incident on {subject}: {title} — stops work',
          },
          action: 'Report an incident',
          field: {
            title: 'What happened',
            description: 'Detail',
            subject: 'About',
            severity: 'Severity',
            blocking: 'Stops work',
          },
          value: {
            yes: 'Yes',
          },
          where: 'Incident about',
          outcome: 'Incident {code} created',
        },
      },
      title: 'Incidents',
      subtitle: 'What is wrong, from the moment somebody sees it until somebody says what happened',
      // La explicacion entera del modulo. El TEXTO vive en `help/<idioma>.md`
      // —el mismo fichero que compila el manual del cliente—; aqui solo van
      // las dos frases que el indice de la ayuda necesita antes de abrirlo.
      help: {
        title: "Incidents",
        subtitle: "Writing down that something is wrong before anybody has decided anything, and recording what happened",
      },
      detailTitle: 'Incident',
      newTitle: 'New incident',
      dictate: {
        title: 'Dictate it',
        subtitle: 'Record it and the form fills itself. Then you check it and save.',
        clip: 'The voice note',
        read: 'Fill in what I said',
        heard: 'What was heard',
        silence: 'Nothing was audible. Record it again, closer to your mouth.',
        which: '"{words}": there are several. Which one?',
        notFound: 'I could not find "{words}". Pick it by hand below.',
        keeps: 'The recording is attached to the incident when you save it. Remove it with the × if you would rather not.',
        clipFailed: 'The incident was saved, but the voice note could not be attached.',
      },
      editTitle: 'Edit the incident',
      blocking: 'Stops work',
      stale: 'Untouched',

      related: {
        open: 'What is wrong',
        customer: 'Incidents',
      },

      actions: {
        new: 'New incident',
        take: 'I will take it',
        createOrder: 'Create order',
        resolve: 'Resolve',
        dismiss: 'Dismiss',
        edit: 'Edit',
        reopen: 'Reopen',
      },

      status: {
        open: 'Open',
        in_progress: 'In progress',
        resolved: 'Resolved',
        dismissed: 'Dismissed',
      },

      severity: { low: 'Minor', medium: 'Normal', high: 'Serious', critical: 'Critical' },

      subjectType: {
        asset: 'Asset',
        property: 'Property',
        production_order: 'Manufacturing order',
        vehicle: 'Vehicle',
        location: 'Location',
        project: 'Project',
        work: 'Job',
      },

      stats: {
        open: 'Open',
        critical: 'Critical',
        blocking: 'Stopping work',
        stale: 'Untouched',
      },

      filters: {
        search: 'Search',
        searchPlaceholder: 'What it says, the code, or what it is about',
        status: 'State',
        statusAny: 'Any',
        severity: 'Severity',
        severityAny: 'Any',
        type: 'Kind',
        typeAny: 'Any',
        subjectType: 'About',
        subjectTypeAny: 'Anything',
        blocking: 'Work stopped',
        blockingOnly: 'Only the ones stopping work',
      },

      columns: {
        code: 'Code',
        incident: 'Incident',
        severity: 'Severity',
        status: 'State',
        assignee: 'Owner',
        age: 'For',
      },

      age: {
        today: 'Today',
        underHour: 'Under an hour',
        hours: '{hours} hours',
        days: '{days} days',
        months: '{months} months ago',
      },

      empty: {
        title: 'Nothing logged',
        hint: 'Write down what you see and it stays here until somebody says what happened.',
      },

      pager: { previous: 'Previous', next: 'Next', page: 'Page {page} of {of}' },

      outcome: {
        resolved: 'Resolved by {who} on {when}',
        dismissed: 'Dismissed by {who} on {when}',
        somebody: 'somebody',
      },

      facts: {
        title: 'Facts',
        about: 'About',
        reported: 'When it happened',
        reportedBy: 'Who saw it',
        assignee: 'Owner',
        nobody: 'Nobody',
        openFor: 'Open for',
        took: 'Took',
        customer: 'Customer',
        customerUnnamed: 'See the customer',
        imported: 'Origin',
        importedFrom: 'Imported from {system}',
        importedFromCode: 'Imported from {system} ({code})',
        recordedThere: 'recorded there on {date}',
      },

      history: {
        title: 'History',
        reported: 'Incident opened',
        updated: 'Edited',
        assigned: 'Assigned',
        note: 'Note',
        severity: 'Severity: from {from} to {to}',
        add: 'Add',
        addPlaceholder: 'Say how it is going',
      },

      files: {
        title: 'Photos and papers',
        empty: 'Nothing attached. A photo says at a glance what a sentence takes a paragraph to explain.',
        add: 'Attach',
        uploading: 'Uploading…',
        remove: 'Remove',
        full: 'It already has {max} files, which is the limit. Remove one to add another.',
      },

      confirm: {
        take: 'Take this incident?',
        detachTitle: 'Remove the file',
        detachMessage: '"{name}" will no longer be on this incident.',
        reopenTitle: 'Reopen the incident',
        reopenMessage: 'Reopening is for one that was not actually fixed, or was closed by mistake. If it happened again, open a new one: that way you keep how long it held.',
      },

      resolve: {
        title: 'Resolve the incident',
        what: 'What was done',
        hint: 'This sentence is what somebody reads the next time the same thing happens.',
        placeholder: 'Changed the seal and tightened the fitting',
        cancel: 'Back',
        confirm: 'Resolve',
      },

      dismiss: {
        title: 'Dismiss the incident',
        why: 'Why it will not be dealt with',
        hint: 'Dismissing is not deleting: the incident keeps this reason and who gave it.',
        placeholder: 'It was the sight glass level, not a leak',
        cancel: 'Back',
        confirm: 'Dismiss',
      },

      toast: {
        reported: 'Incident opened',
        saved: 'Saved',
        taken: 'It is yours',
        resolved: 'Resolved',
        dismissed: 'Dismissed',
        reopened: 'Reopened',
        attached: 'File attached',
      },

      form: {
        subtitle: 'What you saw, and what it is about',
        about: 'What it is about',
        aboutHint: 'First, because it is the only part nobody can work out afterwards.',
        subjectType: 'Kind of thing',
        subjectFree: 'Type it in',
        subject: 'Which one',
        subjectPlaceholder: 'Find the machine, the site or the order',
        subjectName: 'About',
        subjectNameHint: 'Write it the way you call it. Just as good as picking it from a list.',
        subjectNamePlaceholder: 'Press 3',
        needsSubject: 'Say what it is about: without that, it is a sentence nobody can file.',
        what: 'What happened',
        title: 'In one line',
        titleHint: 'The way you would tell a colleague.',
        titlePlaceholder: 'Leaking oil down the side',
        description: 'Detail',
        type: 'Kind',
        typeAny: 'No kind',
        when: 'When it happened',
        whenHint: 'If it was earlier, say so: everything measured hangs off this date.',
        howBad: 'How bad',
        howBadHint: 'Severity decides who gets told. Not telling them about everything is what makes the alert get read.',
        severity: 'Severity',
        blocking: 'Work stopped',
        blockingHint: 'Only if work really cannot go on.',
        blockingLabel: 'Stops work going on',
        assignee: 'Owner',
        assigneeHint: 'Optional. With nobody on it, it waits for somebody to take it.',
        assigneePlaceholder: 'Find a person',
        media: 'Photos and videos',
        mediaHint: 'Up to {max} files, counting the voice note. They are uploaded one by one when you save. It is what tells this one apart from the other three times somebody wrote the same thing.',
        mediaFailed: 'The incident was saved, but one file could not be uploaded. Attach it from its page. | The incident was saved, but {count} files could not be uploaded. Attach them from its page.',
        save: 'Save',
        discard: 'Discard',
        leaveTitle: 'You are leaving the incident half written',
        leaveMessage: 'What you typed has not been saved.',
        leaveConfirm: 'Leave anyway',
        leaveCancel: 'Stay here',
      },

      dashboard: {
        open: 'Open incidents',
        openHelp: 'What is wrong and nobody has dealt with yet',
        critical: 'Critical',
        criticalHelp: 'The open ones marked critical. Its own tile because "40 open" hides "2 critical"',
        blocking: 'Stopping work',
        blockingHelp: 'The open ones that are stopping work right now',
        stale: 'Untouched',
        staleHelp: 'Open for longer than the configured days. It measures whether the module is used or only filled',
        mine: 'My incidents',
        mineHelp: 'The open ones you own',
      },

      notifications: {
        category: 'Incidents',
        reported: {
          low: 'Minor incident',
          medium: 'Incident',
          high: 'Serious incident',
          critical: 'Critical incident',
          body: '{where}: {what} ({code})',
        },
        assigned: {
          title: 'An incident is yours',
          body: '{where}: {what} ({code})',
        },
        resolved: {
          title: 'What you reported has been fixed',
          body: '{code} · {what} — {why}',
        },
        dismissed: {
          title: 'What you reported will not be dealt with',
          body: '{code} · {what} — {why}',
        },
      },
    },

    assistant: {
      openers: {
        incidents: {
          'what-is-open': 'What is still unresolved?',
          'stopping-work': 'Is anything stopping work right now?',
          'worst-thing': 'What has given us the most trouble this year?',
        },
      },
    },

    permissions: {
      incidents: {
        read: 'See the incidents and what is wrong with each thing',
        report: 'Open incidents and add notes and photos',
        handle: 'Take them, change their severity, resolve them and dismiss them',
        import: 'Import the incidents of another system',
      },
    },

    settings: {
      ns: {
        incidents: {
          title: 'Incidents',
          subtitle: 'How you sort what goes wrong, and who gets told',
          types: {
            label: 'The kinds of incident you log',
            help: 'Yours, not ours, and optional: leave it empty and an incident is just the line somebody wrote. Filling it in is what makes "what fails most" answerable.',
          },
          alertRoles: {
            label: 'Who is told when one is reported',
            help: 'Leave it empty and nobody is told: the incident is still on the list, it just does not ring anywhere.',
          },
          notifyFrom: {
            label: 'Tell them from this severity up',
            help: 'A company where every scratch pings the plant manager is a company where the plant manager mutes it, and then the critical one goes nowhere either.',
          },
          staleDays: {
            label: 'Days before an open one counts as forgotten',
            help: 'Only used to draw attention to it: nothing closes or escalates on its own.',
          },
          maxAttachments: {
            label: 'Files one incident can carry',
            help: 'Photos, videos, voice notes and papers together. The form stops offering the camera at the limit, and the server refuses one more.',
          },
          resolveFromWorkOrder: {
            label: 'Resolve it when the work order raised from it is completed',
            help: 'Off, the completed order only leaves a note on the incident and somebody resolves it saying what was done. On, it resolves itself with what the reports said; if they said nothing, the note stays.',
          },
        },
      },
    },

    audit: {
      actions: {
        incidents: {
          incident_reported: 'Incident opened',
          incident_updated: 'Incident edited',
          incident_assigned: 'Incident assigned',
          incident_severity: 'Severity changed',
          incident_in_progress: 'Incident taken',
          incident_resolved: 'Incident resolved',
          incident_dismissed: 'Incident dismissed',
          incident_open: 'Incident reopened',
          incident_noted: 'Note on an incident',
          incident_attached: 'File attached to an incident',
          incident_detached: 'File removed from an incident',
          incident_imported: 'Incident imported from another system',
        },
      },
    },
  },

  fr: {
    // Como se llama este modulo en las pantallas de la plataforma
    // (el selector de la demo, los roles, la auditoria). Sin esto
    // salen listando el slug en ingles.
    modules: { incidents: 'Incidents' },
    incidents: {
      // Los avisos de la mañana y de la tarjeta «Hoy» (backend/nudges.js).
      nudges: {
        critical: 'un incident critique ouvert | {count} incidents critiques ouverts',
        blocking: 'un incident qui bloque le travail | {count} incidents qui bloquent le travail',
      },
      assistant: {
        // La respuesta que contestan las reglas (queries.js).
        query: {
          open: {
            some: '{count} incidents sont ouverts : {list}.',
            none: 'Aucun incident n’est ouvert.',
          },
        },
        resolve: {
          action: 'Résoudre un incident',
          field: { incident: 'Incident', resolution: 'Ce qui a été fait', severity: 'Gravité' },
          where: 'Incident concernant',
          notFound: 'Cet incident est introuvable',
          alreadyClosed: 'Il est déjà clos',
          outcome: 'Incident {code} résolu',
        },
        report: {
          summary: {
            plain: 'Nouvel incident sur {subject} : {title}',
            blocking: 'Nouvel incident sur {subject} : {title} — bloque le travail',
          },
          action: 'Signaler un incident',
          field: {
            title: 'Ce qui se passe',
            description: 'Détail',
            subject: 'Concerne',
            severity: 'Gravité',
            blocking: 'Bloque le travail',
          },
          value: {
            yes: 'Oui',
          },
          where: 'Incident concernant',
          outcome: 'Incident {code} créé',
        },
      },
      title: 'Incidents',
      subtitle: 'Ce qui va mal, depuis que quelqu’un le voit jusqu’à ce que quelqu’un dise ce qui s’est passé',
      // La explicacion entera del modulo. El TEXTO vive en `help/<idioma>.md`
      // —el mismo fichero que compila el manual del cliente—; aqui solo van
      // las dos frases que el indice de la ayuda necesita antes de abrirlo.
      help: {
        title: "Incidents",
        subtitle: "Noter que quelque chose ne va pas avant que quiconque ait décidé, et garder par écrit ce qui s’est passé",
      },
      detailTitle: 'Incident',
      newTitle: 'Nouvel incident',
      dictate: {
        title: 'La dicter',
        subtitle: 'Enregistrez-la et le formulaire se remplit. Vous relisez, puis vous enregistrez.',
        clip: 'La note vocale',
        read: 'Remplir avec ce que j\'ai dit',
        heard: 'Ce qui a été entendu',
        silence: 'Rien n\'a été entendu. Réenregistrez, plus près de la bouche.',
        which: '« {words} » : il y en a plusieurs. Lequel ?',
        notFound: 'Je n\'ai pas trouvé « {words} ». Choisissez-le à la main ci-dessous.',
        keeps: 'L\'enregistrement reste attaché à l\'incident une fois enregistré. Retirez-le avec la × si vous ne le voulez pas.',
        clipFailed: 'L\'incident a été enregistré, mais la note vocale n\'a pas pu être jointe.',
      },
      editTitle: 'Modifier l’incident',
      blocking: 'Arrête le travail',
      stale: 'Sans suite',

      related: {
        open: 'Ce qui ne va pas',
        customer: 'Incidents',
      },

      actions: {
        new: 'Nouvel incident',
        take: 'Je le prends',
        createOrder: 'Créer un ordre',
        resolve: 'Résoudre',
        dismiss: 'Écarter',
        edit: 'Modifier',
        reopen: 'Rouvrir',
      },

      status: {
        open: 'Ouvert',
        in_progress: 'En cours',
        resolved: 'Résolu',
        dismissed: 'Écarté',
      },

      severity: { low: 'Mineur', medium: 'Normal', high: 'Grave', critical: 'Critique' },

      subjectType: {
        asset: 'Équipement',
        property: 'Site',
        production_order: 'Ordre de fabrication',
        vehicle: 'Véhicule',
        location: 'Emplacement',
        project: 'Projet',
        work: 'Chantier',
      },

      stats: {
        open: 'Ouverts',
        critical: 'Critiques',
        blocking: 'Arrêtent le travail',
        stale: 'Sans suite',
      },

      filters: {
        search: 'Rechercher',
        searchPlaceholder: 'Ce qui est écrit, le code ou ce que ça concerne',
        status: 'État',
        statusAny: 'Tous',
        severity: 'Gravité',
        severityAny: 'Toutes',
        type: 'Type',
        typeAny: 'Tous',
        subjectType: 'Concerne',
        subjectTypeAny: 'Tout',
        blocking: 'Travail arrêté',
        blockingOnly: 'Uniquement ceux qui arrêtent le travail',
      },

      columns: {
        code: 'Code',
        incident: 'Incident',
        severity: 'Gravité',
        status: 'État',
        assignee: 'Responsable',
        age: 'Depuis',
      },

      age: {
        today: 'Aujourd’hui',
        underHour: 'Moins d’une heure',
        hours: '{hours} heures',
        days: '{days} jours',
        months: 'Il y a {months} mois',
      },

      empty: {
        title: 'Rien de signalé',
        hint: 'Notez ce que vous voyez : ça reste ici jusqu’à ce que quelqu’un dise ce qui s’est passé.',
      },

      pager: { previous: 'Précédent', next: 'Suivant', page: 'Page {page} sur {of}' },

      outcome: {
        resolved: 'Résolu par {who} le {when}',
        dismissed: 'Écarté par {who} le {when}',
        somebody: 'quelqu’un',
      },

      facts: {
        title: 'Données',
        about: 'Concerne',
        reported: 'Quand c’est arrivé',
        reportedBy: 'Qui l’a vu',
        assignee: 'Responsable',
        nobody: 'Personne',
        openFor: 'Ouvert depuis',
        took: 'A pris',
        customer: 'Client',
        customerUnnamed: 'Voir le client',
        imported: 'Origine',
        importedFrom: 'Importé de {system}',
        importedFromCode: 'Importé de {system} ({code})',
        recordedThere: 'enregistré là-bas le {date}',
      },

      history: {
        title: 'Historique',
        reported: 'Incident ouvert',
        updated: 'Modifié',
        assigned: 'Attribué',
        note: 'Note',
        severity: 'Gravité : de {from} à {to}',
        add: 'Ajouter',
        addPlaceholder: 'Dites où ça en est',
      },

      files: {
        title: 'Photos et papiers',
        empty: 'Rien de joint. Une photo dit d’un coup d’œil ce qu’une phrase met un paragraphe à expliquer.',
        add: 'Joindre',
        uploading: 'Envoi…',
        remove: 'Retirer',
        full: 'Il a déjà {max} fichiers, c’est la limite. Retirez-en un pour en ajouter un autre.',
      },

      confirm: {
        take: 'Vous prenez cet incident ?',
        detachTitle: 'Retirer le fichier',
        detachMessage: '« {name} » ne sera plus sur cet incident.',
        reopenTitle: 'Rouvrir l’incident',
        reopenMessage: 'Rouvrir sert pour un incident qui n’a pas vraiment été réglé, ou fermé par erreur. Si c’est arrivé de nouveau, ouvrez-en un autre : on garde ainsi combien de temps ça a tenu.',
      },

      resolve: {
        title: 'Résoudre l’incident',
        what: 'Ce qui a été fait',
        hint: 'Cette phrase est ce que quelqu’un lira la prochaine fois que ça arrivera.',
        placeholder: 'Joint remplacé et raccord resserré',
        cancel: 'Retour',
        confirm: 'Résoudre',
      },

      dismiss: {
        title: 'Écarter l’incident',
        why: 'Pourquoi on n’y touchera pas',
        hint: 'Écarter n’est pas supprimer : l’incident garde ce motif et qui l’a donné.',
        placeholder: 'C’était le niveau du voyant, pas une fuite',
        cancel: 'Retour',
        confirm: 'Écarter',
      },

      toast: {
        reported: 'Incident ouvert',
        saved: 'Enregistré',
        taken: 'Il est à vous',
        resolved: 'Résolu',
        dismissed: 'Écarté',
        reopened: 'Rouvert',
        attached: 'Fichier joint',
      },

      form: {
        subtitle: 'Ce que vous avez vu, et sur quoi',
        about: 'Ce que ça concerne',
        aboutHint: 'En premier, car c’est la seule partie qu’on ne peut pas deviner après.',
        subjectType: 'Type de chose',
        subjectFree: 'L’écrire à la main',
        subject: 'Laquelle',
        subjectPlaceholder: 'Cherchez la machine, le site ou l’ordre',
        subjectName: 'Concerne',
        subjectNameHint: 'Écrivez-le comme vous l’appelez. Cela vaut autant que de le choisir dans une liste.',
        subjectNamePlaceholder: 'Presse 3',
        needsSubject: 'Dites ce que ça concerne : sinon, c’est une phrase que personne ne peut classer.',
        what: 'Ce qui s’est passé',
        title: 'En une ligne',
        titleHint: 'Comme vous le diriez à un collègue.',
        titlePlaceholder: 'Fuite d’huile sur le côté',
        description: 'Détail',
        type: 'Type',
        typeAny: 'Sans type',
        when: 'Quand c’est arrivé',
        whenHint: 'Si c’était avant, indiquez-le : tout ce qui est mesuré dépend de cette date.',
        howBad: 'Gravité',
        howBadHint: 'La gravité décide qui est prévenu. Ne pas prévenir pour tout est ce qui fait que l’alerte est lue.',
        severity: 'Gravité',
        blocking: 'Travail arrêté',
        blockingHint: 'Seulement si on ne peut vraiment pas continuer.',
        blockingLabel: 'Empêche de continuer à travailler',
        assignee: 'Responsable',
        assigneeHint: 'Facultatif. Sans personne dessus, il attend que quelqu’un le prenne.',
        assigneePlaceholder: 'Cherchez une personne',
        media: 'Photos et vidéos',
        mediaHint: 'Jusqu’à {max} fichiers, note vocale comprise. Ils sont envoyés un par un à l’enregistrement. C’est ce qui distingue celui-ci des trois autres fois où quelqu’un a écrit la même chose.',
        mediaFailed: 'L’incident a été enregistré, mais un fichier n’a pas pu être envoyé. Joignez-le depuis sa fiche. | L’incident a été enregistré, mais {count} fichiers n’ont pas pu être envoyés. Joignez-les depuis sa fiche.',
        save: 'Enregistrer',
        discard: 'Abandonner',
        leaveTitle: 'Vous laissez l’incident à moitié écrit',
        leaveMessage: 'Ce que vous avez saisi n’a pas été enregistré.',
        leaveConfirm: 'Sortir quand même',
        leaveCancel: 'Rester ici',
      },

      dashboard: {
        open: 'Incidents ouverts',
        openHelp: 'Ce qui va mal et que personne n’a encore traité',
        critical: 'Critiques',
        criticalHelp: 'Les ouverts marqués critiques. À part, car « 40 ouverts » cache « 2 critiques »',
        blocking: 'Arrêtent le travail',
        blockingHelp: 'Les ouverts qui empêchent de travailler en ce moment',
        stale: 'Sans suite',
        staleHelp: 'Ouverts depuis plus de jours que configuré. Mesure si le module est utilisé ou seulement rempli',
        mine: 'Mes incidents',
        mineHelp: 'Les ouverts dont vous êtes responsable',
      },

      notifications: {
        category: 'Incidents',
        reported: {
          low: 'Incident mineur',
          medium: 'Incident',
          high: 'Incident grave',
          critical: 'Incident critique',
          body: '{where} : {what} ({code})',
        },
        assigned: {
          title: 'Un incident vous revient',
          body: '{where} : {what} ({code})',
        },
        resolved: {
          title: 'Ce que vous avez signalé est réparé',
          body: '{code} · {what} — {why}',
        },
        dismissed: {
          title: 'Ce que vous avez signalé ne sera pas traité',
          body: '{code} · {what} — {why}',
        },
      },
    },

    assistant: {
      openers: {
        incidents: {
          'what-is-open': 'Qu’est-ce qui n’est pas résolu ?',
          'stopping-work': 'Y a-t-il quelque chose qui arrête le travail ?',
          'worst-thing': 'Qu’est-ce qui nous a donné le plus de mal cette année ?',
        },
      },
    },

    permissions: {
      incidents: {
        read: 'Voir les incidents et ce qui ne va pas sur chaque chose',
        report: 'Ouvrir des incidents et y ajouter des notes et des photos',
        handle: 'Les prendre, changer leur gravité, les résoudre et les écarter',
        import: 'Importer les incidents d’un autre système',
      },
    },

    settings: {
      ns: {
        incidents: {
          title: 'Incidents',
          subtitle: 'Comment vous classez ce qui va mal, et qui est prévenu',
          types: {
            label: 'Les types d’incident que vous notez',
            help: 'Les vôtres, pas les nôtres, et facultatif : laissez vide et un incident est la phrase que quelqu’un a écrite. Le remplir est ce qui rend « ce qui casse le plus » répondable.',
          },
          alertRoles: {
            label: 'Qui est prévenu quand un incident est ouvert',
            help: 'Laissez vide et personne n’est prévenu : l’incident reste sur la liste, il ne sonne simplement nulle part.',
          },
          notifyFrom: {
            label: 'Prévenir à partir de cette gravité',
            help: 'Une entreprise où chaque égratignure prévient le chef d’atelier est une entreprise où le chef d’atelier coupe les alertes, et la critique n’arrive plus non plus.',
          },
          staleDays: {
            label: 'Jours avant qu’un incident ouvert compte comme oublié',
            help: 'Sert uniquement à attirer l’attention : rien ne se ferme ni ne s’escalade tout seul.',
          },
          maxAttachments: {
            label: 'Fichiers qu’un incident peut porter',
            help: 'Photos, vidéos, notes vocales et documents, tout compris. Le formulaire cesse de proposer l’appareil photo à la limite, et le serveur en refuse un de plus.',
          },
          resolveFromWorkOrder: {
            label: 'La résoudre quand l’ordre de travail qui en est issu est terminé',
            help: 'Désactivé, l’ordre terminé laisse seulement une note sur l’incident et quelqu’un le résout en disant ce qui a été fait. Activé, il se résout seul avec ce qu’ont écrit les rapports ; s’ils n’ont rien écrit, la note reste.',
          },
        },
      },
    },

    audit: {
      actions: {
        incidents: {
          incident_reported: 'Incident ouvert',
          incident_updated: 'Incident modifié',
          incident_assigned: 'Incident attribué',
          incident_severity: 'Gravité modifiée',
          incident_in_progress: 'Incident pris',
          incident_resolved: 'Incident résolu',
          incident_dismissed: 'Incident écarté',
          incident_open: 'Incident rouvert',
          incident_noted: 'Note sur un incident',
          incident_attached: 'Fichier joint à un incident',
          incident_detached: 'Fichier retiré d’un incident',
          incident_imported: 'Incident importé d’un autre système',
        },
      },
    },
  },
}

// El vocabulario de la analítica se mezcla aparte: es un CONTRATO con
// `backend/analytics.js` y no copia de una pantalla. Ver ese fichero.
// What the assistant panel says while this module's tools run: another
// contract with the backend (backend/assistant.js), in its own file.
import { withToolLabels } from './i18n/toolLabels.js'
// The reasons and field codes its errors carry (frontend/i18n/errorReasons.js).
import { withErrorReasons } from './i18n/errorReasons.js'

export const messages = withErrorReasons(withToolLabels(withAnalytics(baseMessages, 'incidents')))
